import { useState } from "react";
import { Link, Outlet, useLocation } from "@tanstack/react-router";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { ThemeToggle } from "./ThemeToggle";
import { cn } from "@/lib/utils";

const NAV = [
  { to: "/", label: "Matches" },
  { to: "/criteria", label: "Criteria" },
  { to: "/stats", label: "Stats" },
];

export function Layout() {
  const loc = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="relative min-h-screen">
      {/* The beam glow behind the header */}
      <div className="beam-glow pointer-events-none absolute inset-x-0 top-0 h-64 -z-10" />

      <header className="border-b border-border bg-background/60 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-4 md:px-6 py-3">
          <Link to="/" className="flex items-center gap-2.5 text-foreground">
            <Logo className="h-7 w-7 text-foreground" />
            <span className="text-lg font-semibold tracking-tight">Beacon</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => {
              const active =
                n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={cn(
                    "relative rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    active
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {n.label}
                  {active && (
                    <span className="absolute inset-x-3 -bottom-px h-px bg-primary shadow-beam-sm" />
                  )}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <button
              className="md:hidden rounded-md p-1.5 text-muted-foreground hover:text-foreground transition-colors"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              onClick={() => setMenuOpen((prev) => !prev)}
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown nav */}
        {menuOpen && (
          <div className="md:hidden absolute inset-x-0 top-full bg-background border-b border-border z-40">
            {NAV.map((n) => {
              const active =
                n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to);
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setMenuOpen(false)}
                  className={cn(
                    "block px-4 py-3 text-sm font-medium transition-colors",
                    active
                      ? "text-foreground"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {n.label}
                </Link>
              );
            })}
          </div>
        )}
      </header>

      <main className="mx-auto max-w-6xl px-4 md:px-6 py-8">
        <Outlet />
      </main>
    </div>
  );
}
